import { PortsMap } from '../hooks/useNetworkPorts'

export interface VMCreationParams {
  name: string
  hostname: string
  sourceType: string
  image: any[]
  installerIso: any[]
  driverIso: any[]
  volume: any[]
  volumeSize: string
  volumeType: string
  volumeSnapshot: any[]
  deleteOnTermination: boolean
  flavor: any[]
  networks: any[]
  selectedPorts: { [networkId: string]: string }
  fixedIPs: { [networkId: string]: string }
  attachVolumes: any[]
  sshKey: string | null
  serverGroup: { id: string } | null
  useCloudInit: boolean
  cloudInitYaml: string
  useSecurityGroups: boolean
  securityGroups: { name: string }[]
  metadata: { key: string; value: string }[]
  enableHotplugging: boolean
  hotplugMemory: number | null
  hotplugMemoryMax: number
  hotplugVcpu: number | null
  hotplugVcpuMax: number
  availabilityZone: string | null
  priority: string
  count: number
}

export interface BuildVMBodyOptions {
  simpleNetworkPorts?: PortsMap
  managedNetworkPorts?: PortsMap
}

/** Metadata key for simple (L2-only) pre-created port IDs. Used during VM deletion to clean up ports. */
export const LAYER_2_PORT_METADATA_KEY = 'layer-2-network-ports'

/** Metadata key for managed network pre-created port IDs (subnet-selected). Used during VM deletion to clean up ports. */
export const MANAGED_NETWORK_PORT_METADATA_KEY = 'managed-network-ports'

/**
 * Build the common VM request body for OpenStack Nova API
 *
 * This function constructs the server creation payload including:
 * - Boot source (image, volume, snapshot)
 * - Flavor configuration
 * - Network configuration (with Simple Network port support)
 * - Cloud-init user data
 * - Security groups
 * - Metadata (including hotplug settings)
 * - Scheduler hints (server groups)
 */
export const buildVMRequestBody = (
  params: VMCreationParams,
  options: BuildVMBodyOptions = {},
): any => {
  const { simpleNetworkPorts = {}, managedNetworkPorts = {} } = options

  // Combined map used for network config (both types need a pre-created port UUID)
  const allPreCreatedPorts = { ...simpleNetworkPorts, ...managedNetworkPorts }

  // Encode cloud-init script
  const encodedScript = params.useCloudInit ? btoa(params.cloudInitYaml) : undefined

  // Build security groups array
  const securityGroups = params.useSecurityGroups
    ? params.securityGroups.map((group) => ({ name: group.name }))
    : undefined

  // Build metadata object from key-value pairs
  const metadata = (params.metadata || []).reduce(
    (accum, pair) => ({
      ...accum,
      [pair.key]: pair.value,
    }),
    {},
  )

  // Add hotplug metadata if enabled
  const hotplugMetadata = params.enableHotplugging
    ? {
        HOTPLUG_MEMORY: String(params.hotplugMemory),
        HOTPLUG_CPU: String(params.hotplugVcpu),
      }
    : {}

  // Store pre-created port IDs under separate keys so they can be deleted when the VM is removed
  const simplePortIds = Object.values(simpleNetworkPorts).join(',') || undefined
  const managedPortIds = Object.values(managedNetworkPorts).join(',') || undefined

  // Combine all metadata
  const combinedMetadata = {
    ...metadata,
    ...hotplugMetadata,
    ...(params.priority !== '' && { 'migration-priority': params.priority }),
    ...(simplePortIds && { [LAYER_2_PORT_METADATA_KEY]: simplePortIds }),
    ...(managedPortIds && { [MANAGED_NETWORK_PORT_METADATA_KEY]: managedPortIds }),
  }

  // Build additional volumes array
  const additionalVolumes = (params.attachVolumes || []).map((volume) => ({
    uuid: volume?.id,
    source_type: 'volume',
    destination_type: 'volume',
    boot_index: -1,
    delete_on_termination: false,
  }))

  // Build block device mapping based on source type
  const buildBlockDeviceMapping = () => {
    if (['image', 'snapshot'].includes(params.sourceType)) {
      return undefined
    }

    if (params.sourceType === 'installFromIso') {
      // "Install from ISO" is really a 2-disk setup:
      // 1) Nova creates a small ISO-backed volume that behaves like a virtual CD/DVD
      // 2) Nova also creates a blank target volume where the OS will be installed
      const installerIsoSizeBytes = params.installerIso?.[0]?.virtual_size || 0
      const installerIsoVolumeSizeGiB = installerIsoSizeBytes
        ? Math.ceil(installerIsoSizeBytes / (1024 * 1024 * 1024))
        : 1
      const isoVolumes = [
        {
          // OS target disk — boot_index=0
          source_type: 'blank',
          destination_type: 'volume',
          volume_size: params.volumeSize,
          boot_index: 0,
          delete_on_termination: false,
          volume_type: params.volumeType || undefined,
        },
        {
          // Installer ISO — boot_index=1
          uuid: params.installerIso?.[0]?.id,
          source_type: 'image',
          destination_type: 'volume',
          volume_size: installerIsoVolumeSizeGiB,
          boot_index: 1,
          device_type: 'cdrom',
          delete_on_termination: true,
        },
      ]

      if (params.driverIso?.[0]?.id) {
        const driverIsoSizeBytes = params.driverIso[0]?.virtual_size || 0
        const driverIsoVolumeSizeGiB = driverIsoSizeBytes
          ? Math.ceil(driverIsoSizeBytes / (1024 * 1024 * 1024))
          : 1
        isoVolumes.push({
          uuid: params.driverIso[0].id,
          source_type: 'image',
          destination_type: 'volume',
          volume_size: driverIsoVolumeSizeGiB,
          boot_index: -1,
          device_type: 'cdrom',
          delete_on_termination: true,
        })
      }

      return [...isoVolumes, ...additionalVolumes]
    }

    const bootDevice =
      params.sourceType === 'existingVolume'
        ? [
            {
              source_type: 'volume',
              destination_type: 'volume',
              boot_index: 0,
              delete_on_termination: false,
              uuid: params.volume[0]?.id,
            },
          ]
        : params.sourceType === 'newVolume'
        ? [
            {
              source_type: 'image',
              destination_type: 'volume',
              boot_index: 0,
              delete_on_termination: params.deleteOnTermination,
              uuid: params.image?.[0]?.id,
              volume_size: params.volumeSize,
              volume_type: params.volumeType ? params.volumeType : undefined,
            },
          ]
        : params.sourceType === 'volumeSnapshot'
        ? [
            {
              source_type: 'snapshot',
              destination_type: 'volume',
              boot_index: 0,
              delete_on_termination: params.deleteOnTermination,
              uuid: params.volumeSnapshot?.[0]?.id,
            },
          ]
        : []

    return [...bootDevice, ...additionalVolumes]
  }

  // Build network configuration
  const buildNetworks = () => {
    return (params.networks || []).map((network) => {
      // Use pre-created port if available (simple L2 network or managed with selected subnet)
      if (allPreCreatedPorts[network.id]) {
        return { port: allPreCreatedPorts[network.id] }
      }

      // Managed Network: Use network UUID or manually selected port
      return {
        uuid: network.id,
        ...(params.selectedPorts[network.id]
          ? params.selectedPorts[network.id] === 'fixedIP'
            ? { fixed_ip: params.fixedIPs[network.id] }
            : { port: params.selectedPorts[network.id] }
          : {}),
      }
    })
  }

  // Check if config_drive should be auto-enabled
  // Config drive is only needed when cloud-init is enabled AND
  // (Simple Networks are used OR DHCP is disabled in any subnet)
  const shouldEnableConfigDrive =
    params.useCloudInit &&
    params.networks.some(
      (network) =>
        network.isSimpleNetwork ||
        network.subnetDetails?.some((subnet: any) => subnet.enable_dhcp === false),
    )

  // Build the final request body
  return {
    server: {
      name: params.name,
      imageRef: ['image', 'snapshot'].includes(params.sourceType)
        ? params.image?.[0]?.id
        : undefined,
      block_device_mapping_v2: buildBlockDeviceMapping(),
      flavorRef: params.flavor?.[0]?.id,
      networks: buildNetworks(),
      key_name: params.sshKey ? (params.sshKey === 'None' ? undefined : params.sshKey) : undefined,
      config_drive: shouldEnableConfigDrive,
      user_data: params.useCloudInit ? encodedScript : undefined,
      security_groups: securityGroups,
      metadata: combinedMetadata,
      ...(params.availabilityZone && { availability_zone: params.availabilityZone }),
      ...(params.hostname && { hostname: params.hostname }),
      min_count: params.count === 1 ? undefined : params.count,
      return_reservation_id: params.count === 1 ? undefined : true,
    },
    'OS-SCH-HNT:scheduler_hints': params.serverGroup
      ? { group: params.serverGroup?.id }
      : undefined,
  }
}

export default buildVMRequestBody
